/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import IdentityHeader from "./IdentityHeader";
import ProfileNudge from "./ProfileNudge";
import StatsBoard from "./StatsBoard";
import "./IndividualDashboard.css";

// Individual · my dashboard. A donor's own giving, answering three questions
// in the order they get asked: what did my money do, where is the receipt, and
// which nonprofits have I backed over time.
//
// Shares the "dash" section id with the nonprofit dashboard — see
// renderSection in Marketplace.js, which picks between them on persona.
class IndividualDashboard extends Component {
  render() {
    const vm = this.props.vm;
    const d = vm.myDash;
    if (d.loading || d.error) {
      return (
        <div className="Marketplace-card Marketplace-status">
          {d.error ? vm.m.dashError : vm.m.dashLoading}
        </div>
      );
    }
    const st = d.stats;
    return (
      <React.Fragment>
        <ProfileNudge nudge={vm.profileNudge} />
        {/* Who the donor is and the two figures they scan for. Only on real
            data — the sample has no account behind it. The nonprofit
            dashboard draws the same band from the same component; a donor
            passes no logo and no tagline, so it renders the shorter card. */}
        {!!st && <IdentityHeader profile={st.profile} />}

        {/* The same tiles, twelve-month chart and split the nonprofit
            dashboard uses, read from the donor's side. */}
        {!!st && <StatsBoard data={st} m={vm.m} />}

        <div className="Marketplace-MyDash">
          <div className="Marketplace-MyDash-main">
            {/* Absent on real data: nothing on a payment records a cause, so a
              split here would be invented. See CP-285. */}
            {!!d.causes.length && (
              <div className="Marketplace-card">
                <div className="Marketplace-cardTitle">{d.causesTitle}</div>
                {/* One stacked bar rather than a bar per cause: the whole is the
                  donor's giving, and each segment is its share of it. */}
                <div className="Marketplace-MyDash-split">
                  {d.causes.map((c) => (
                    <div
                      className="Marketplace-MyDash-splitPart"
                      key={c.id}
                      style={{ width: c.w, background: c.bar }}
                    >
                      <span className="Marketplace-MyDash-splitLabel">{c.name}</span>
                    </div>
                  ))}
                </div>
                <div className="Marketplace-MyDash-legend">
                  {d.causes.map((c) => (
                    <span className="Marketplace-MyDash-legendItem" key={c.id}>
                      <span
                        className="Marketplace-MyDash-dot"
                        style={{ background: c.dot }}
                      />
                      <span className="Marketplace-MyDash-legendPct">{c.pctLabel}</span>
                      <span className="Marketplace-MyDash-legendName">
                        {c.name} · {c.amount}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="Marketplace-card">
              {/* No download button here — every row carries its own receipt
                  link, and the page head opens the most recent one. */}
              <div className="Marketplace-cardTitle">{vm.m.myDashEvery}</div>
              <div className="Marketplace-MyDash-receiptNote">{vm.m.myDashReceiptNote}</div>
              {d.empty && (
                <div className="Marketplace-MyDash-empty">{vm.m.myDashEmpty}</div>
              )}
              {d.truncated && (
                <div className="Marketplace-MyDash-truncated">{vm.m.myDashTruncated}</div>
              )}
              <div className="Marketplace-MyDash-rows">
                {d.history.map((h) => (
                  <div className="Marketplace-MyDash-row" key={h.id}>
                    <div className="Marketplace-MyDash-rowDate">{h.date}</div>
                    <div className="Marketplace-MyDash-rowBody">
                      <div className="Marketplace-MyDash-rowWho">{h.who}</div>
                      {!!h.project && (
                        <span className="Marketplace-MyDash-rowProject">{h.project}</span>
                      )}
                      <span
                        className="Marketplace-MyDash-rowKind"
                        style={{ background: h.kindBg, color: h.kindFg }}
                      >
                        {h.kindLabel}
                      </span>
                    </div>
                    <span className="Marketplace-MyDash-rowAmount">{h.amount}</span>
                    {/* A real receipt where the payment has one; the sample rows
                      and any payment still settling do not. */}
                    {h.receiptUrl ? (
                      <a
                        className="Marketplace-MyDash-rowReceipt"
                        href={h.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {vm.m.myDashRowReceipt}
                      </a>
                    ) : (
                      <span className="Marketplace-MyDash-rowReceipt Marketplace-MyDash-rowReceipt--none">
                        {vm.m.myDashRowReceipt}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          <div className="Marketplace-MyDash-side">
            <div className="Marketplace-card">
              <div className="Marketplace-MyDash-allHeader">
                <span className="Marketplace-cardTitle">{vm.m.myDashAllTime}</span>
                {/* Re-slices what is already loaded — no refetch, and the giving
                  history above is never narrowed by it. */}
                <div className="Marketplace-MyDash-ranges">
                  {d.ranges.map((r) => (
                    <div
                      className="Marketplace-MyDash-range"
                      key={r.id}
                      onClick={r.go}
                      style={{ borderColor: r.bd, background: r.bg, color: r.fg }}
                    >
                      {r.label}
                    </div>
                  ))}
                </div>
              </div>
              <div className="Marketplace-MyDash-allNote">
                {vm.fill(vm.m.myDashRanked, { range: d.rangeLabel })}
                <span className="Marketplace-MyDash-allLink" onClick={d.goDiscover}>
                  {vm.m.myDashFindMore}
                </span>
              </div>
              <div className="Marketplace-MyDash-allTime">
                {d.allTime.map((a) => (
                  <div key={a.id}>
                    <div className="Marketplace-MyDash-allHead">
                      <span className="Marketplace-MyDash-allRank">{a.rank}</span>
                      <span className="Marketplace-MyDash-allName">{a.name}</span>
                      <span className="Marketplace-MyDash-allTotal">{a.total}</span>
                      <span className="Marketplace-MyDash-allPct">{a.pctLabel}</span>
                    </div>
                    <div className="Marketplace-bar">
                      <div
                        className="Marketplace-barFill"
                        style={{ width: a.w, background: a.bar }}
                      />
                    </div>
                    {/* Which of this nonprofit's projects the money funded —
                      behind a click, so the panel reads as a ranking first.
                      Bars are relative to the nonprofit's own total. */}
                    {!!a.projects.length && (
                      <div className="Marketplace-MyDash-allToggle" onClick={a.toggle}>
                        {a.projectsLabel}
                        <span
                          className={
                            "Marketplace-MyDash-allCaret" +
                            (a.open ? " Marketplace-MyDash-allCaret--open" : "")
                          }
                        />
                      </div>
                    )}
                    {!!a.projects.length && a.open && (
                      <div className="Marketplace-MyDash-allProjects">
                        {a.projects.map((pr) => (
                          <div className="Marketplace-MyDash-allProject" key={pr.id}>
                            <div className="Marketplace-MyDash-allProjectHead">
                              <span className="Marketplace-MyDash-allProjectName">
                                {pr.name}
                              </span>
                              <span className="Marketplace-MyDash-allProjectAmount">
                                {pr.amount}
                              </span>
                            </div>
                            <div className="Marketplace-MyDash-allProjectBar">
                              <div
                                className="Marketplace-MyDash-allProjectFill"
                                style={{ width: pr.w }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="Marketplace-MyDash-allSince">
                      {a.cause ? a.cause + " · " + a.since : a.since}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

IndividualDashboard.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default IndividualDashboard;
